'use client';

import { ethers } from 'ethers';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { WalletProviderType } from '../constants';
import { DEFAULT_WALLET_DETAILS } from '../constants/walletProvider';
import { BalanceData, SupportedNetworksData } from '../interface';
import { StorageService } from '../services';
import { useAppKitModalContent } from './AppKitModalProvider';
import { useMetaMask } from './MetaMaskWalletProvider';

export type WalletType = 'ethereum' | 'solana';

export type ProviderType = ethers.providers.ExternalProvider | ethers.providers.JsonRpcFetchFunc;

export interface WalletDetailsProps {
  address: string;
  networkDetails: SupportedNetworksData;
  balance: BalanceData;
  provider: any;
  providerType: string;
  refetchBalance: boolean;
}

export type SupportedChainIDs = 1 | 4 | 137 | 80001 | 11155111 | 5 | 84532 | 8453;

export interface WalletDetailsContextType {
  walletDetails: WalletDetailsProps;
  switchNetworkDetails: SwitchNetworkDetailProp;
  setWalletDetails(f: WalletDetailsProps | ((prev: WalletDetailsProps) => WalletDetailsProps)): void;
  SetSwitchNetworkDetails(
    f: SwitchNetworkDetailProp | ((prev: SwitchNetworkDetailProp) => SwitchNetworkDetailProp)
  ): void;
}

export interface SwitchNetworkDetailProp {
  isSwitch: boolean;
  chainId: SupportedChainIDs;
  switchStatus: 'switch' | 'switching' | 'switched';
}

const WalletDetailsContext = createContext<WalletDetailsContextType | undefined>(undefined);

export const WalletDetailsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { walletProvider } = useAppKitModalContent();
  const { getMetaMaskProvider } = useMetaMask();

  const [walletDetails, setWalletDetails] = useState<WalletDetailsProps>(DEFAULT_WALLET_DETAILS);
  const [switchNetworkDetails, SetSwitchNetworkDetails] = useState<SwitchNetworkDetailProp>({
    isSwitch: false,
    chainId: 11155111,
    switchStatus: 'switch'
  });

  const initialSessionData = useMemo(() => {
    return (StorageService.walletDetails.getValue() || DEFAULT_WALLET_DETAILS) as WalletDetailsProps;
  }, []);

  //the provider selection logic to use in callbacks
  const getProviderForType = useCallback(
    (providerType: string) => {
      if (providerType === WalletProviderType.METAMASK) {
        return getMetaMaskProvider();
      }

      if (providerType === WalletProviderType.WALLET_CONNECT && walletProvider) {
        return new ethers.providers.Web3Provider(walletProvider as ProviderType);
      }

      return null;
    },
    [walletProvider, getMetaMaskProvider]
  );

  //To update while page hard refresh
  useEffect(() => {
    const walletProvider = getProviderForType(initialSessionData?.providerType);

    setWalletDetails((prev) => {
      if (prev?.provider) return prev;

      return { ...initialSessionData, provider: initialSessionData?.address ? walletProvider : null };
    });
  }, [getProviderForType, initialSessionData]);

  //To update while connecting and disconnecting the wallet
  useEffect(() => {
    // Create a BroadcastChannel
    const channel = new BroadcastChannel('walletDetailsChannel');

    // Listen for messages from other tabs
    channel.onmessage = (event) => {
      const updatedStorage = event.data ? event.data : (DEFAULT_WALLET_DETAILS as WalletDetailsProps);
      const walletProvider = getProviderForType(updatedStorage?.providerType);

      setWalletDetails({
        ...updatedStorage,
        provider: updatedStorage?.address ? walletProvider : null
      });
    };

    // Clean up on unmount
    return () => {
      channel.close();
    };
  }, [getProviderForType]);

  return (
    <WalletDetailsContext.Provider
      value={{
        walletDetails,
        switchNetworkDetails,
        setWalletDetails,
        SetSwitchNetworkDetails
      }}
    >
      {children}
    </WalletDetailsContext.Provider>
  );
};

export const useWalletDetails = () => {
  const context = useContext(WalletDetailsContext);

  if (!context) {
    throw new Error('useWalletDetails must be used within a WalletDetailsProvider');
  }

  return context;
};
